import React, { Component } from "react";
import NeuralBackground from "./NeuralBackground";

const SKILL_LAYOUT = [
  {
    name: "JavaScript & TypeScript",
    match: ["javascript", "typescript"],
    span: "md:col-span-2",
    icon: "terminal",
    iconColor: "bg-blue-500/10 text-blue-400",
    barColor: "bg-primary glow-accent",
    badge: "Mastery",
    description:
      "Expert in scalable frontend architectures & robust type systems.",
    fillIcon: true,
  },
  {
    name: "Python & AI/ML",
    match: ["python"],
    span: "",
    icon: "data_exploration",
    iconColor: "bg-primary/15 text-primary",
    barColor: "bg-primary",
  },
  {
    name: "Elasticsearch",
    match: ["elasticsearch"],
    span: "",
    icon: "dns",
    iconColor: "bg-cyan-500/10 text-cyan-400",
    barColor: "bg-cyan-400",
  },
  {
    name: "Node.js & React",
    match: ["node", "react"],
    span: "md:col-span-2",
    icon: "layers",
    iconColor: "bg-violet-500/10 text-violet-300",
    barColor: "bg-violet-400",
    badge: "Advanced",
    description:
      "Building performant web apps with React, Node.js & modern state management.",
  },
  {
    name: "LLMs & RAG",
    match: ["llm", "rag"],
    span: "md:col-span-2",
    icon: "auto_awesome",
    iconColor: "text-primary",
    centered: true,
    description:
      "Production LLM agents, RAG pipelines, and customer-embedded AI delivery.",
  },
];

class Home extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chatInput: "",
      statusDots: 0,
      showChat: true,
      lastQuestion: "Show me your recent software engineering projects.",
    };
    this.statusInterval = null;
    this.chatInputRef = React.createRef();
  }

  componentDidMount() {
    this.statusInterval = setInterval(() => {
      this.setState((prev) => ({
        statusDots: (prev.statusDots + 1) % 4,
      }));
    }, 1000);
  }

  componentWillUnmount() {
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
    }
  }

  handleSubmit = (event) => {
    if (event) event.preventDefault();
    const question = this.state.chatInput.trim();
    if (!question) return;
    this.setState({
      lastQuestion: question,
      chatInput: "",
      showChat: true,
    });
  };

  handleQuickAction = (action) => {
    const { main } = this.props;
    if (action === "portfolio") {
      this.setState({
        lastQuestion: "Show me your recent software engineering projects.",
        showChat: true,
      });
      const el = document.getElementById("projects");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (action === "tech") {
      const el = document.getElementById("expertise");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (action === "resume" && main && main.resumedownload) {
      window.open(main.resumedownload, "_blank", "noopener,noreferrer");
    }
  };

  getSkillLevel(skillName, skills) {
    if (!skills) return "80%";
    const found = skills.find((skill) => {
      const lower = skill.name.toLowerCase();
      return (
        lower === skillName.toLowerCase() ||
        lower.includes(skillName.toLowerCase().split(" ")[0].toLowerCase())
      );
    });
    return found ? found.level : "80%";
  }

  renderNav() {
    const { main } = this.props;
    const name = main.name;
    const github = main.github;
    const linkedin = main.social
      ? main.social.find((s) => s.name === "linkedin")
      : null;
    const dots = ".".repeat(this.state.statusDots);

    return (
      <nav className="fixed top-0 left-0 w-full z-50 bg-background/80 backdrop-blur-xl border-b border-outline-variant/30">
        <div className="flex justify-between items-center max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop h-20">
          <a
            href="#home"
            className="signature-logo text-3xl md:text-4xl text-primary hover:text-on-surface transition-colors"
            aria-label={`${name} home`}
          >
            {name}
          </a>
          <div className="flex items-center gap-6">
            <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-surface-container rounded-full border border-outline-variant/30">
              <span className="w-2 h-2 rounded-full bg-green-500 status-dot"></span>
              <span className="font-label-md text-label-md text-on-surface-variant">
                Available for Hire{dots}
              </span>
            </div>
            <div className="flex items-center gap-4">
              {github && (
                <a
                  className="text-on-surface-variant hover:text-primary transition-colors"
                  href={github}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                >
                  <span className="material-symbols-outlined">code</span>
                </a>
              )}
              {linkedin && (
                <a
                  className="text-on-surface-variant hover:text-primary transition-colors"
                  href={linkedin.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <span className="material-symbols-outlined">person</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </nav>
    );
  }

  renderProjectCard(project, index) {
    const projectImage = "images/portfolio/" + project.image;
    const isComingSoon = project.comingSoon || !project.url;
    const techLine = project.tags ? project.tags.join(", ") : project.category;

    return (
      <div
        key={index}
        className="w-full bg-surface-container-low border border-outline-variant/20 rounded-xl overflow-hidden group hover:border-primary/50 transition-colors"
      >
        <div className="h-48 w-full overflow-hidden">
          <div
            className="w-full h-full bg-cover bg-center group-hover:scale-105 transition-transform duration-500"
            style={{ backgroundImage: `url('${projectImage}')` }}
          ></div>
        </div>
        <div className="p-6">
          <h3 className="font-headline-md text-headline-md text-on-surface mb-1">
            {project.title}
          </h3>
          <p className="text-on-surface-variant text-body-md mb-4">{techLine}</p>
          {!isComingSoon ? (
            <a
              className="inline-flex items-center gap-2 font-label-md text-label-md text-primary hover:gap-3 transition-all"
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Live{" "}
              <span className="material-symbols-outlined text-sm">
                open_in_new
              </span>
            </a>
          ) : (
            <span className="inline-flex items-center gap-2 font-label-md text-label-md text-on-surface-variant">
              Coming Soon
            </span>
          )}
        </div>
      </div>
    );
  }

  renderExpertise() {
    const skills = this.props.resume ? this.props.resume.skills : [];

    return (
      <section id="expertise" className="mt-section-gap">
        <h2 className="font-headline-lg text-headline-lg text-on-surface mb-12">
          Core Expertise
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {SKILL_LAYOUT.map((item) => {
            const level = this.getSkillLevel(item.name, skills);
            if (item.centered) {
              return (
                <div
                  key={item.name}
                  className={`${item.span} bg-surface-container rounded-xl p-8 border border-outline-variant/30 flex flex-col items-center justify-center gap-4 group hover:border-primary/30 transition-all text-center`}
                >
                  <span
                    className={`material-symbols-outlined text-4xl ${item.iconColor}`}
                  >
                    {item.icon}
                  </span>
                  <h3 className="font-headline-md text-headline-md text-on-surface">
                    {item.name}
                  </h3>
                  <p className="text-on-surface-variant max-w-xs">
                    {item.description}
                  </p>
                </div>
              );
            }

            return (
              <div
                key={item.name}
                className={`${item.span} bg-surface-container rounded-xl p-8 border border-outline-variant/30 flex flex-col justify-between group hover:border-primary/30 transition-all`}
              >
                <div className="flex justify-between items-start mb-12">
                  <div
                    className={`w-12 h-12 rounded-lg ${item.iconColor} flex items-center justify-center`}
                  >
                    <span
                      className="material-symbols-outlined text-3xl"
                      style={
                        item.fillIcon
                          ? { fontVariationSettings: "'FILL' 1" }
                          : undefined
                      }
                    >
                      {item.icon}
                    </span>
                  </div>
                  {item.badge && (
                    <span className="font-code text-code text-primary">
                      {item.badge}
                    </span>
                  )}
                </div>
                <div>
                  {item.description ? (
                    <h3 className="font-headline-md text-headline-md text-on-surface mb-2">
                      {item.name}
                    </h3>
                  ) : (
                    <h4 className="font-headline-md text-headline-md text-on-surface mb-2">
                      {item.name}
                    </h4>
                  )}
                  {item.description && (
                    <p className="text-on-surface-variant mb-6">
                      {item.description}
                    </p>
                  )}
                  <div
                    className={`w-full h-1 bg-outline-variant/30 rounded-full overflow-hidden ${
                      item.description ? "" : "mt-4"
                    }`}
                  >
                    <div
                      className={`h-full ${item.barColor}`}
                      style={{ width: level }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  render() {
    const { main, portfolio } = this.props;
    if (!main) return null;

    const name = main.name;
    const profilepic = "images/" + main.image;
    const projects = portfolio && portfolio.projects ? portfolio.projects : [];
    const firstName = name.split(" ")[0];

    return (
      <div
        id="home"
        className="relative min-h-screen bg-background text-on-surface"
      >
        {this.renderNav()}

        <div
          className="absolute inset-x-0 top-0 h-screen overflow-hidden"
          aria-hidden="true"
        >
          <NeuralBackground />
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent to-background"></div>
        </div>

        <main className="relative pt-32 pb-section-gap max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop">
          <section className="flex flex-col items-center text-center mb-16">
            <div className="relative w-32 h-32 md:w-40 md:h-40 mb-8 p-1 rounded-full bg-gradient-to-tr from-primary via-primary-container to-cyan-300/70 glow-accent">
              <div className="w-full h-full rounded-full overflow-hidden bg-surface-container">
                <img
                  className="w-full h-full object-cover object-top"
                  src={profilepic}
                  alt={`${name} profile`}
                />
              </div>
            </div>
            <h1 className="font-display text-headline-lg-mobile md:text-display text-on-surface mb-6 max-w-3xl leading-tight">
              Hello, I&apos;m {name}. Ask my AI agent anything about my
              engineering work.
            </h1>
          </section>

          <div className="max-w-4xl mx-auto space-y-12">
            <form className="relative group" onSubmit={this.handleSubmit}>
              <div className="glow-accent-border flex items-center bg-surface-container-low border border-outline-variant/50 rounded-full px-6 py-4 transition-all duration-300">
                <input
                  ref={this.chatInputRef}
                  className="w-full bg-transparent border-none focus:ring-0 text-body-lg font-body-lg text-on-surface placeholder:text-on-surface-variant/50"
                  placeholder="Ask me about my recent projects or tech stack..."
                  type="text"
                  value={this.state.chatInput}
                  onChange={(e) => this.setState({ chatInput: e.target.value })}
                />
                <button
                  type="submit"
                  className="ml-4 w-10 h-10 rounded-full bg-primary flex items-center justify-center text-on-primary hover:scale-105 active:scale-95 transition-transform"
                  aria-label="Send"
                >
                  <span className="material-symbols-outlined">arrow_forward</span>
                </button>
              </div>
            </form>

            <div className="flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => this.handleQuickAction("portfolio")}
                className="flex items-center gap-2 px-5 py-2.5 bg-surface-container-high rounded-full border border-outline-variant/30 hover:bg-surface-variant transition-colors font-label-md text-label-md"
              >
                <span>📁</span> View Portfolio
              </button>
              <button
                type="button"
                onClick={() => this.handleQuickAction("tech")}
                className="flex items-center gap-2 px-5 py-2.5 bg-surface-container-high rounded-full border border-outline-variant/30 hover:bg-surface-variant transition-colors font-label-md text-label-md"
              >
                <span>🛠️</span> Check Tech Stack
              </button>
              <button
                type="button"
                onClick={() => this.handleQuickAction("resume")}
                className="flex items-center gap-2 px-5 py-2.5 bg-surface-container-high rounded-full border border-outline-variant/30 hover:bg-surface-variant transition-colors font-label-md text-label-md"
              >
                <span>📄</span> Download Resume
              </button>
            </div>
          </div>

          {this.state.showChat && (
            <div id="projects" className="space-y-8 mt-12">
              <div className="max-w-4xl mx-auto space-y-8">
                <div className="flex justify-end">
                  <div className="max-w-[80%] bg-primary-container/15 border border-primary/30 text-on-surface rounded-2xl rounded-tr-none px-6 py-4 shadow-sm">
                    <p className="font-body-md">{this.state.lastQuestion}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 border border-primary/40 text-primary flex items-center justify-center text-[10px] font-semibold tracking-wide">
                    AI
                  </div>
                  <span className="font-label-md text-label-md text-on-surface-variant">
                    {firstName}&apos;s Assistant
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project, index) =>
                  this.renderProjectCard(project, index)
                )}
              </div>
            </div>
          )}

          {this.renderExpertise()}
        </main>
      </div>
    );
  }
}

export default Home;
